/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

/*
    Hello there! This is a demonstration of how to override a file from the base template.

    It's necessary that the module export interface remain consistent,
    as other files in the base template rely on constants.js, thus we
    import the underlying constants.js, modifies it and re-export it.
*/

import {
    DEFAULT_LIMIT_VALUES,
    DEFAULT_SEARCH_PARAMS
} from '@salesforce/retail-react-app/app/constants'

// original value is 25
DEFAULT_LIMIT_VALUES[0] = 25
DEFAULT_SEARCH_PARAMS.limit = 32
// Relevance is frontend-only: no sorting rule is sent to SFCC.
delete DEFAULT_SEARCH_PARAMS.sort
export const RELEVANCE_SORT_ID = 'relevance'

// Cache time constants - imported from separate file to allow SSR usage
// (ssr.js can't import from this file due to ESM re-exports from retail-react-app)
export {
    MAX_CACHE_AGE_CMS,
    MAX_CACHE_AGE_S,
    MAX_CACHE_AGE_M,
    MAX_CACHE_AGE_L,
    MAX_CACHE_AGE_XL,
    STALE_WHILE_REVALIDATE_S,
    STALE_WHILE_REVALIDATE_M,
    STALE_WHILE_REVALIDATE_L,
    STALE_WHILE_REVALIDATE_XL
} from './cache-times-configs'

export const DEFAULT_LOCALE = 'en-GB'
export const CUSTOM_HOME_TITLE = '🎉 Hello Extensible React Template!'
export const THEME_COLOR = '#FFFFFF'

// Constants used for product searching.
export {DEFAULT_LIMIT_VALUES, DEFAULT_SEARCH_PARAMS}

// Constants for Search Component
export const RECENT_SEARCH_LIMIT = 5
export const RECENT_SEARCH_KEY = 'recent-search-key'
export const RECENT_SEARCH_MIN_LENGTH = 3
export const DID_YOU_MEAN_SEARCH_LIMIT = 3

export const SHIPPING_COUNTRY_CODES = [
    {value: 'CA', label: 'Canada'},
    {value: 'US', label: 'United States'},
    {value: 'GB', label: 'United Kingdom'},
    {value: 'UK', label: 'United Kingdom'}
]

export const SITE_COUNTRY_MAP = {
    DynamiteGarageCA: 'CA',
    DynamiteGarageUS: 'US',
    DynamiteGarageUK: 'GB'
}

export const VICKYAI_RECOMMENDATIONS = {
    PRODUCT_RECOMMENDATION: 'recommendations',
    PRODUCT_BOUGHT_TOGETHER: 'bought-together',
    PRODUCT_SIMILARS: 'similars'
}

export const UGC_PLACEMENT = {
    HOMEPAGE: 'homepage',
    PDP: 'pdp',
    LANDING: 'landing'
}

export const UGC_CURSOR_URL_PARAM = 'cursor'
export const UGC_DEFAULT_PAGE_SIZE = 24 

export const UGC_SORT_OPTIONS = {
    NEWEST: 'newest',
    POPULAR: 'popular'
}

export const UGC_SORT_PARAM_MAP = {
    [UGC_SORT_OPTIONS.NEWEST]: 'Latest',
    [UGC_SORT_OPTIONS.POPULAR]: 'Optimized'
}

// UGC env var base names. Brand-scoped ones resolve as `${brandCode}_${base}`,
// where brandCode comes from toBrandCode ('GRG' | 'DYN') — e.g. GRG_BV_UGC_ENABLED,
// DYN_BV_UGC_LANDING_LIMIT. TOTAL_COUNT_ENABLED is the exception: a single global
// key with no brand scoping, so read it directly (isUgcTotalCountEnabled), never
// through the brand-prefixing getUgcFlag.
export const UGC_FLAG_NAMES = {
    BV_UGC_ENABLED: 'BV_UGC_ENABLED',
    LANDING_ENABLED: 'BV_UGC_LANDING_ENABLED',
    PDP_ENABLED: 'BV_UGC_PDP_ENABLED',
    TOTAL_COUNT_ENABLED: 'BV_UGC_TOTAL_COUNT_ENABLED'
}

export const UGC_LIMIT_NAMES = {
    LANDING: 'BV_UGC_LANDING_LIMIT',
    PDP: 'BV_UGC_PDP_LIMIT',
    EXPANDED_VIEW: 'BV_UGC_EXPANDED_VIEW_LIMIT',
    HOMEPAGE: 'BV_UGC_HOME_PAGE_LIMIT'
}

export const UGC_CATEGORY_ATTRIBUTE = 'c_isUGC'

export const UGC_CATEGORY_LOOKUP_DEPTH = 3

export const FOR_YOU_CATEGORY_ATTRIBUTE = 'c_isForYou'

export const FOR_YOU_CATEGORY_LOOKUP_DEPTH = UGC_CATEGORY_LOOKUP_DEPTH

export const FOR_YOU_PAGE_SIZE = 32

export const FOR_YOU_MAX_PRODUCTS = 100

export const FYP_RECOMMENDATION_MAX_COUNT = 250

export const FOR_YOU_FLAG_NAME = 'VICKY_FYP_LANDING_ENABLED'

export const FYP_LIMIT_NAMES = {
    PAGE_SIZE: 'FYP_PAGE_SIZE',
    MAX_PRODUCTS: 'FYP_MAX_PRODUCT_COUNT'
}

export const FYP_GARAGE_COLOR_ATTRIBUTE = 'c_fypGarageColor'
export const FYP_DYNAMITE_COLOR_ATTRIBUTE = 'c_fypDynamiteColor'

export const FYP_NAV_COLOR_DEFAULT = '#000000'

export const FILTER_ACCORDION_STATE = 'filters-expanded-index'

export const PUBLIC_VARIABLES = [
    'ADDRESS_SUGGESTIONS_ENABLED',
    'ADDRESS_VALIDATION_ENABLED',
    'ADDRESS_SERVICES_DEBOUNCE_INTERVAL',
    'ADYEN_CLIENT_KEY',
    'ADYEN_USE_KLARNA_WIDGET',
    'PRODUCTS_PER_PAGE_MOBILE',
    'PRODUCTS_PER_PAGE_DESKTOP',
    'PRODUCT_TILE_MEDIA_CAROUSEL_ENABLED',
    'WISHLIST_COUNT_ON_PRODUCT_TILE_RENDERING_ENABLED',
    'SHOW_BOPIS',
    'ADYEN_ENNVIRONMENT',
    'KLARNA_OSMSDK_ENV',
    'KLARNA_CLIENT_ID',
    'KLARNA_ENVIRONMENT',
    'KLARNA_CLIENT_ID',
    'SMS_OPTIN_LOYALTY_ENABLED',
    'DYN_BV_UGC_ENABLED',
    'GRG_BV_UGC_ENABLED',
    'DYN_BV_UGC_LANDING_ENABLED',
    'GRG_BV_UGC_LANDING_ENABLED',
    'DYN_BV_UGC_PDP_ENABLED',
    'GRG_BV_UGC_PDP_ENABLED',
    'BV_UGC_TOTAL_COUNT_ENABLED',
    'BV_UGC_HOME_PAGE_LIMIT',
    'DYN_BV_UGC_LANDING_LIMIT',
    'DYN_BV_UGC_LANDING_LIMIT',
    'DYN_BV_UGC_PDP_LIMIT',
    'GRG_BV_UGC_PDP_LIMIT',
    'DYN_BV_UGC_EXPANDED_VIEW_LIMIT',
    'GRG_BV_UGC_EXPANDED_VIEW_LIMIT'
]

export const SIZE_REFINEMENTS_FALLBACK = [
    'c_clothingSize',
    'c_bottomSize',
    'c_shoeSize',
    'c_shoeSize',
    'c_otherSize',
    'c_accessoriesSize'
]

export const SIZES = [
    {value: 'XXS', label: 'XXS'},
    {value: 'XS', label: 'XS'},
    {value: 'S', label: 'S'},
    {value: 'M', label: 'M'},
    {value: 'L', label: 'L'},
    {value: 'XL', label: 'XL'}
]

export const CAT_MENU_SITEMAP_NAV_SSR_DEPTH = 3

export const RESOURCE_BADGE_MAPPING = {
    'onlineexclusive': {
        id: 'badge.label.online_exclusive',
        defaultMessage: 'Online Exclusive'
    },
    'finalsale': {
        id: 'badge.label.final_sale',
        defaultMessage: 'Final Sale'
    },
    'new': {
        id: 'badge.label.new',
        defaultMessage: 'New'
    },
    'earlyaccess': {
        id: 'badge.label.early_access',
        defaultMessage: 'Early Access'
    },
    'elevee': {
        id: 'badge.label.elevee',
        defaultMessage: 'Elevee'
    },
    'limitededition': {
        id: 'badge.label.limited_edition',
        defaultMessage: 'Limited Edition'
    },
    'extendedlengths': {
        id: 'badge.label.extended_lengths',
        defaultMessage: 'Extended Length'
    },
    'premiumwool': {
        id: 'badge.label.premium_wool',
        defaultMessage: 'Premium Wool'
    },
    'luxelounge': {
        id: 'badge.label.luxe_lounge',
        defaultMessage: 'Luxe Lounge'
    },
    'collab': {
        id: 'badge.label.collab',
        defaultMessage: 'Collab'
    },
    'rsvp': {
        id: 'badge.label.rsvp',
        defaultMessage: 'RSVP'
    },
    'sculptd': {
        id: 'badge.label.sculptd',
        defaultMessage: "Sculpt'd"
    },
    'backinstock': {
        id: 'badge.label.back_in_stock',
        defaultMessage: "Back In Stock"
    },
    'toohotforsale': {
        id: 'badge.label.too_hot_for_sale',
        defaultMessage: "Too Hot For Sale"
    },
    'essentials': {
        id: 'badge.label.essentials',
        defaultMessage: "Essentiæls"
    },
    'comingsoon': {
        id: 'badge.label.coming_soon',
        defaultMessage: "Coming Soon"
    },
    'waterresistant': {
        id: 'badge.label.water_resistant',
        defaultMessage: "Water Resistant"
    }
}

const BADGE_KEY_ALIASES = {
    "sculpt'd": 'sculptd',
    'essenti\u00e6ls': 'essentials'
}

export const normalizeResourceBadgeKey = (badge = '') => {
    if (typeof badge !== 'string') {
        return ''
    }

    const normalizedBadge = badge.trim().toLowerCase()
    if (BADGE_KEY_ALIASES[normalizedBadge]) {
        return BADGE_KEY_ALIASES[normalizedBadge]
    }

    return normalizedBadge
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[\u2019']/g, '')
        .replace(/[^a-z0-9]/g, '')
}

export * from '@salesforce/retail-react-app/app/constants'
